import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import type { Topic } from 'hb-react/types';

type SetContextStateAction<T> = (value: Partial<T>) => void;

const TopicContext = createContext<{
  topic?: Topic;
  updateTopic: SetContextStateAction<Topic>;
}>({
  topic: undefined,
  updateTopic: () => {},
});

export const useTopicContext = () => useContext(TopicContext);

export const TopicProvider = ({
  children,
  topic: initialTopic = {} as Topic,
}: PropsWithChildren<{ topic?: Topic }>) => {
  const [topic, setTopic] = useState(() => initialTopic);

  const providerState = useMemo(
    () => ({
      topic,
      updateTopic: (topic) => {
        setTopic((prev) => ({ ...prev, ...topic }));
      },
    }),
    [topic],
  );

  return (
    <TopicContext.Provider value={providerState}>
      {children}
    </TopicContext.Provider>
  );
};

export const withTopicContext = (Component) => (props) => {
  const contextValue = useTopicContext();

  return <Component {...props} {...contextValue} />;
};

export default TopicContext;
